import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../Config';

const PieceList = () => {
    const [pieces, setPieces] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userRole, setUserRole] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    const token = localStorage.getItem('token');

    useEffect(() => {
        const userData = localStorage.getItem('user');
        if (userData) {
            try {
                const parsedUser = JSON.parse(userData);
                setUserRole(parsedUser.role || '');
            } catch (err) {
                console.error('Erreur parsing user:', err);
            }
        }
        
        fetchPieces();
    }, []);

    const fetchPieces = async () => {
        try {
            setLoading(true);
            // Utiliser l'endpoint /pieces-remplacees
            const response = await axios.get(`${API_URL}/pieces-remplacees`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            console.log('Pièces chargées:', response.data);
            setPieces(response.data);
            setError(null);
        } catch (err) {
            console.error('Erreur détaillée:', err.response?.data || err.message);
            setError('Erreur lors du chargement des pièces: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette pièce?')) return;

        // Vérifier si l'utilisateur a le droit de supprimer
        if (!userRole || !['mecanicien', 'magasinier'].includes(userRole)) {
            alert('Vous n\'avez pas les droits pour supprimer une pièce');
            return;
        }

        try {
            await axios.delete(`${API_URL}/pieces-remplacees/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchPieces();
        } catch (err) {
            alert('Erreur lors de la suppression: ' + (err.response?.data?.message || err.message));
            console.error(err);
        }
    };

    // Calculer le total des pièces
    const totalPiecesValue = pieces.reduce((total, piece) => {
        const prix = parseFloat(piece.prix || 0);
        const quantite = parseFloat(piece.quantite || 1);
        return total + (prix * quantite);
    }, 0);

    // Filtrer les pièces par recherche
    const filteredPieces = pieces.filter(piece => {
        if (!searchTerm) return true;
        
        const term = searchTerm.toLowerCase();
        return (
            piece.nom_piece?.toLowerCase().includes(term) ||
            piece.reparation?.description_finale?.toLowerCase().includes(term) ||
            piece.id.toString().includes(term)
        );
    });

    const formatCurrency = (amount) => {
        return parseFloat(amount || 0).toFixed(2) + ' MAD';
    };

    if (loading) {
        return (
            <div className="min-h-screen  py-8">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-700 mx-auto"></div>
                        <p className="mt-4 text-gray-600">Chargement des pièces...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen  py-8">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="bg-white border border-gray-300 rounded-lg p-6">
                        <h2 className="text-xl font-semibold text-red-700 mb-2">Erreur</h2>
                        <p className="text-gray-600 mb-4">{error}</p>
                        <button
                            onClick={fetchPieces}
                            className="bg-gray-800 text-white px-4 py-2 rounded hover:bg-gray-700 transition text-sm"
                        >
                            Réessayer
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen  py-8">
            <div className="max-w-7xl mx-auto px-4">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex justify-between items-center mb-6">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Pièces remplacées</h1>
                            <p className="text-gray-600 mt-1">
                                {pieces.length} pièce(s) • Total: {formatCurrency(totalPiecesValue)}
                            </p>
                        </div>
                        
                        {userRole && ['mecanicien', 'magasinier'].includes(userRole) && (
                            <Link
                                to="/pieces/nouvelle"
                                className="bg-gray-800 text-white px-4 py-2 rounded hover:bg-gray-700 transition text-sm"
                            >
                                + Ajouter une pièce
                            </Link>
                        )}
                    </div>
                     <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="bg-white border border-gray-300 rounded p-4">
                                <div className="text-sm text-gray-500 mb-1">Total pièces</div>
                                <div className="text-2xl font-bold text-gray-900">{pieces.length}</div>
                            </div>
                            <div className="bg-white border border-gray-300 rounded p-4">
                                <div className="text-sm text-gray-500 mb-1">Valeur totale</div>
                                <div className="text-2xl font-bold text-gray-900">{formatCurrency(totalPiecesValue)}</div>
                            </div>
                            <div className="bg-white border border-gray-300 rounded p-4">
                                <div className="text-sm text-gray-500 mb-1">Moyenne par pièce</div>
                                <div className="text-2xl font-bold text-gray-900">
                                    {formatCurrency(pieces.length > 0 ? totalPiecesValue / pieces.length : 0)}
                                </div>
                            </div>
                            <div className="bg-white border border-gray-300 rounded p-4">
                                <div className="text-sm text-gray-500 mb-1">Pièces affichées</div>
                                <div className="text-2xl font-bold text-gray-900">{filteredPieces.length}</div>
                            </div>
                        </div>

                    {/* Barre de recherche */}
                    <div className="mb-6 mt-4">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Rechercher par nom de pièce, description..."
                                className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-gray-500 text-sm"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <svg className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                    </div>
                </div>

                {/* Tableau des pièces */}
                {filteredPieces.length === 0 ? (
                    <div className="bg-white border border-gray-300 rounded-lg p-8 text-center">
                        <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <h3 className="text-lg font-medium text-gray-600 mb-2">
                            {searchTerm ? 'Aucune pièce trouvée' : 'Aucune pièce enregistrée'}
                        </h3>
                        {userRole && ['mecanicien', 'magasinier'].includes(userRole) && !searchTerm && (
                            <Link
                                to="/pieces/nouvelle"
                                className="text-gray-600 hover:text-gray-800 text-sm font-medium"
                            >
                                Ajouter la première pièce
                            </Link>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="bg-white border border-gray-300 rounded-lg overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="">
                                        <tr>
                                            <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Pièce
                                            </th>
                                            <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Quantité
                                            </th>
                                            <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Prix unitaire
                                            </th>
                                            <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Total
                                            </th>
                                            
                                            <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {filteredPieces.map((piece) => {
                                            const pieceTotal = parseFloat(piece.prix || 0) * (piece.quantite || 1);
                                            
                                            return (
                                                <tr key={piece.id} className="hover:">
                                                    <td className="py-3 px-6">
                                                        <div className="text-gray-900 font-medium">{piece.nom_piece}</div>
                                                    </td>
                                                    <td className="py-3 px-6">
                                                        <span className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded text-sm">
                                                            {piece.quantite || 1}
                                                        </span>
                                                    </td>
                                                    <td className="py-3 px-6 text-gray-700">
                                                        {piece.prix ? formatCurrency(piece.prix) : 'N/A'}
                                                    </td>
                                                    <td className="py-3 px-6">
                                                        <span className="font-medium text-gray-900">
                                                            {formatCurrency(pieceTotal)}
                                                        </span>
                                                    </td>
                                                    
                                                    <td className="py-3 px-6">
                                                        {userRole && ['mecanicien', 'magasinier'].includes(userRole) && (
                                                            <button
                                                                onClick={() => handleDelete(piece.id)}
                                                                className="text-red-600 hover:text-red-800 text-sm"
                                                            >
                                                                Supprimer
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot className="">
                                        <tr>
                                            <td colSpan={3} className="py-3 px-6 text-right text-sm font-medium text-gray-700">
                                                Total général :
                                            </td>
                                            <td className="py-3 px-6">
                                                <span className="font-bold text-gray-900">{formatCurrency(totalPiecesValue)}</span>
                                            </td>
                                            <td colSpan={2}></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>

                        {/* Statistiques */}
                        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="bg-white border border-gray-300 rounded p-4">
                                <div className="text-sm text-gray-500 mb-1">Total pièces</div>
                                <div className="text-2xl font-bold text-gray-900">{pieces.length}</div>
                            </div>
                            <div className="bg-white border border-gray-300 rounded p-4">
                                <div className="text-sm text-gray-500 mb-1">Valeur totale</div>
                                <div className="text-2xl font-bold text-gray-900">{formatCurrency(totalPiecesValue)}</div>
                            </div>
                            <div className="bg-white border border-gray-300 rounded p-4">
                                <div className="text-sm text-gray-500 mb-1">Moyenne par pièce</div>
                                <div className="text-2xl font-bold text-gray-900">
                                    {formatCurrency(pieces.length > 0 ? totalPiecesValue / pieces.length : 0)}
                                </div>
                            </div>
                            <div className="bg-white border border-gray-300 rounded p-4">
                                <div className="text-sm text-gray-500 mb-1">Pièces affichées</div>
                                <div className="text-2xl font-bold text-gray-900">{filteredPieces.length}</div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default PieceList;